import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Minus, Percent, Plus, ShoppingCart, Trash2, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { trackBeginCheckout } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  ["GB", "United Kingdom"], ["IE", "Ireland"], ["US", "United States"],
  ["CA", "Canada"], ["AU", "Australia"], ["NZ", "New Zealand"],
  ["FR", "France"], ["DE", "Germany"], ["ES", "Spain"], ["IT", "Italy"],
  ["NL", "Netherlands"], ["BE", "Belgium"], ["PT", "Portugal"],
  ["SE", "Sweden"], ["NO", "Norway"], ["DK", "Denmark"], ["CH", "Switzerland"],
  ["AT", "Austria"], ["PL", "Poland"], ["AE", "United Arab Emirates"],
  ["SG", "Singapore"], ["JP", "Japan"], ["ZA", "South Africa"],
];

export default function Cart() {
  const { items, setQty, removeItem, addItem, count } = useCart();
  const { isAuthenticated, openAuth } = useAuth();
  const [products, setProducts] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [paying, setPaying] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [addr, setAddr] = useState({
    full_name: "", line1: "", line2: "", city: "", postcode: "", country: "GB",
  });

  useEffect(() => {
    axios.get(`${API}/store/products`)
      .then(({ data }) => setProducts(data.products))
      .catch(() => setProducts([]));
    axios.get(`${API}/store/bundles`)
      .then(({ data }) => setBundles(data.bundles))
      .catch(() => {});
  }, []);

  const lines = useMemo(() => {
    if (!products) return [];
    return Object.entries(items)
      .map(([id, qty]) => {
        if (id.startsWith("bundle:")) {
          const b = bundles.find((x) => `bundle:${x.id}` === id);
          return b ? {
            id, name: b.name, price_gbp: b.price_gbp,
            image_url: b.items[0]?.image_url, qty, isBundle: true,
            savings_gbp: b.savings_gbp,
          } : null;
        }
        const p = products.find((x) => x.id === id);
        return p ? { ...p, qty } : null;
      })
      .filter(Boolean);
  }, [items, products, bundles]);

  const subtotal = lines.reduce((a, l) => a + l.price_gbp * l.qty, 0);
  const shipping = lines.length === 0 ? 0 : addr.country === "GB" ? 1.99 : 4.99;
  const serviceFee = lines.length === 0 ? 0 : 2.5;
  const discount = promo ? Math.round(subtotal * promo.pct) / 100 : 0;
  const total = subtotal - discount + shipping + serviceFee;

  const applyPromo = async () => {
    if (!isAuthenticated) { openAuth("login"); return; }
    setCheckingPromo(true);
    try {
      const { data } = await axios.post(`${API}/store/validate-promo`, { code: promoInput.trim() });
      if (data.valid) {
        setPromo({ code: promoInput.trim().toUpperCase(), pct: data.pct });
        toast.success(`${data.description} applied!`);
      } else {
        setPromo(null);
        toast.error(data.reason);
      }
    } catch {
      toast.error("Couldn't check that code — try again.");
    } finally { setCheckingPromo(false); }
  };

  // Bundle upsell: cart holds 2+ herbs of a bundle not already in the cart
  const upsell = useMemo(() => {
    const cartHerbIds = new Set(Object.keys(items).filter((id) => !id.startsWith("bundle:")));
    let best = null;
    for (const b of bundles) {
      if (items[`bundle:${b.id}`]) continue;
      const matched = b.items.filter((i) => cartHerbIds.has(i.herb_id));
      if (matched.length >= 2 && (!best || matched.length > best.matched.length)) {
        best = { bundle: b, matched };
      }
    }
    return best;
  }, [items, bundles]);

  const upgradeToBundle = () => {
    upsell.matched.forEach((i) => removeItem(i.herb_id));
    addItem(`bundle:${upsell.bundle.id}`);
    toast.success(`Upgraded to ${upsell.bundle.name} — you save £${upsell.bundle.savings_gbp.toFixed(2)}!`);
  };

  const checkout = async () => {
    if (!isAuthenticated) {
      openAuth("login");
      toast.info("Please log in to complete your order.");
      return;
    }
    for (const [k, label] of [["full_name", "full name"], ["line1", "address"], ["city", "city"], ["postcode", "postcode"]]) {
      if (!addr[k].trim()) { toast.error(`Please enter your ${label}.`); return; }
    }
    setPaying(true);
    trackBeginCheckout();
    try {
      const { data } = await axios.post(`${API}/store/checkout`, {
        items: lines.filter((l) => !l.isBundle).map((l) => ({ herb_id: l.id, quantity: l.qty })),
        bundles: lines.filter((l) => l.isBundle).map((l) => ({ bundle_id: l.id.replace("bundle:", ""), quantity: l.qty })),
        shipping_address: { ...addr, line2: addr.line2 || null },
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
      setPaying(false);
    }
  };

  if (products === null) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <h1 className="font-playfair text-4xl sm:text-5xl font-bold text-gray-900 mb-8">Your Cart</h1>

      {lines.length === 0 ? (
        <div className="text-center py-20" data-testid="cart-empty">
          <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-6">Your cart is empty.</p>
          <Link to="/store">
            <Button className="rounded-full" data-testid="cart-browse-btn">Browse the Herb Store</Button>
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-4">
            {upsell && (
              <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-5" data-testid="bundle-upsell">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
                    <Percent className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">Complete your protocol &amp; save 10%</p>
                    <p className="text-sm text-gray-600 mt-1">
                      You already have <b>{upsell.matched.map((i) => i.name).join(", ")}</b> — upgrade to the full{" "}
                      <b>{upsell.bundle.name}</b> ({upsell.bundle.items.length} herbs) for{" "}
                      <b className="text-emerald-700">£{upsell.bundle.price_gbp.toFixed(2)}</b>{" "}
                      <span className="line-through text-gray-400">£{upsell.bundle.full_price_gbp.toFixed(2)}</span>
                    </p>
                    <Button size="sm" onClick={upgradeToBundle} className="rounded-full mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="bundle-upsell-btn">
                      Upgrade to bundle — save £{upsell.bundle.savings_gbp.toFixed(2)}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {lines.map((l) => (
              <div key={l.id} className="flex items-center gap-2 sm:gap-4 flex-wrap sm:flex-nowrap bg-white border border-gray-100 rounded-2xl p-3 sm:p-4 shadow-sm" data-testid={`cart-line-${l.id}`}>
                {l.image_url && <img src={l.image_url} alt={l.name} className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover shrink-0" />}
                <div className="flex-1 min-w-[140px]">
                  <p className="font-semibold text-gray-900 truncate">{l.name}</p>
                  <p className="text-sm text-gray-500">
                    £{l.price_gbp.toFixed(2)} each
                    {l.isBundle && <span className="text-emerald-600 font-semibold ml-2">Bundle · saves £{l.savings_gbp.toFixed(2)}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setQty(l.id, l.qty - 1)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid="cart-qty-minus"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-6 text-center font-semibold" data-testid="cart-qty">{l.qty}</span>
                  <button onClick={() => setQty(l.id, Math.min(l.qty + 1, l.isBundle ? 5 : 20))} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid="cart-qty-plus"><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <p className="w-16 sm:w-20 text-right font-semibold shrink-0">£{(l.price_gbp * l.qty).toFixed(2)}</p>
                <button onClick={() => removeItem(l.id)} className="p-2 text-rose-500 hover:bg-rose-50 rounded-full shrink-0" data-testid="cart-remove-btn" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm sticky top-24">
              <h2 className="font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2"><Truck className="w-5 h-5 text-primary" /> Delivery details</h2>
              <div className="space-y-3">
                <input value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} placeholder="Full name" data-testid="ship-name-input" className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-primary" />
                <input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Address line 1" data-testid="ship-line1-input" className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-primary" />
                <input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} placeholder="Address line 2 (optional)" data-testid="ship-line2-input" className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-primary" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" data-testid="ship-city-input" className="h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-primary" />
                  <input value={addr.postcode} onChange={(e) => setAddr({ ...addr, postcode: e.target.value })} placeholder="Postcode" data-testid="ship-postcode-input" className="h-11 px-4 rounded-xl border border-gray-200 focus:outline-none focus:border-primary" />
                </div>
                <select value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} data-testid="ship-country-select" className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-primary">
                  {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              </div>

              <div className="border-t border-gray-100 mt-5 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal ({count} items)</span><span data-testid="cart-subtotal">£{subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping {addr.country === "GB" ? "(UK)" : "(International)"}</span>
                  <span data-testid="cart-shipping">£{shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Service &amp; handling</span>
                  <span data-testid="cart-service-fee">£{serviceFee.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-emerald-700 font-medium">
                    <span>Discount ({promo.code})</span>
                    <span data-testid="cart-discount">-£{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <input value={promoInput} onChange={(e) => setPromoInput(e.target.value)}
                         placeholder="Promo code" data-testid="cart-promo-input"
                         className="flex-1 h-9 px-3 rounded-full border border-gray-200 text-sm uppercase focus:outline-none focus:border-primary" />
                  <button onClick={applyPromo} disabled={checkingPromo || promoInput.trim().length < 2}
                          data-testid="cart-promo-apply-btn"
                          className="h-9 px-4 rounded-full bg-gray-900 text-white text-xs font-semibold disabled:opacity-40 hover:bg-gray-800 transition-colors">
                    {checkingPromo ? "…" : "Apply"}
                  </button>
                </div>
                <div className="flex justify-between font-bold text-gray-900 text-base pt-1"><span>Total</span><span data-testid="cart-total">£{total.toFixed(2)}</span></div>
              </div>

              <Button onClick={checkout} disabled={paying} className="w-full h-12 rounded-full mt-5 bg-primary hover:bg-primary/90 font-semibold" data-testid="cart-checkout-btn">
                {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                {paying ? "Redirecting…" : "Pay securely with Stripe"}
              </Button>
              <p className="text-[11px] text-gray-400 text-center mt-3">Secure payment · UK £1.99 / International £4.99 shipping · £2.50 service &amp; handling · Typically ships in 3–7 working days</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
